/**
 * figureItem — 그림 항목(`item`) 하나를 상태로 앉힐 때의 규칙.
 *
 * 이 파일이 있는 이유는 **응답마다 실리는 키가 다르기 때문**이다. 계약 §1-2 는 item 을
 * 「목록·저장·재요청·되돌리기 응답에 공통」이라 적었지만, 2026-08-29 회신 §2 Q7 이 그 위에
 * 조건 하나를 얹었다 — `original_src`(변환 전 픽셀 원본)는 **목록 GET·history GET 의 item 에만**
 * 실리고 PATCH·재요청 응답의 item 에서는 `null` 이다.
 *
 * 규칙을 컴포넌트 안에 두지 않고 여기 둔 이유는 **검사할 수 있어야** 해서다. 조용히 깨지는
 * 종류라 화면으로는 안 잡힌다 — 저장이 성공하고 토스트도 뜨는데 옆 칸의 그림만 사라진다.
 */

/**
 * 응답 item 을 이전 item **위에 병합**한다 — 통째로 갈아 끼우지 않는다.
 *
 * 덮어쓰면 [저장]·[AI 요청]·[되돌리기] 가 **성공하는 순간** 좌측 원본 칸이 비어 버린다.
 * 하필 AI 요청 성공 토스트가 「원본과 나란히 대조해 보세요」라고 말하는 그 순간이라,
 * 화면이 자기가 방금 한 말을 지키지 못하게 된다.
 *
 * restore 응답에 실리는지는 회신이 이름 짓지 않았다(→ 백엔드 질의). 세 경로를 가르지 않고
 * 같은 규칙으로 두면 어느 쪽이 답이어도 화면은 옳다 — 실려 오면 새 값이 이기고, 안 오면 앞 값이 남는다.
 *
 * OCR 의 `crop` 은 받치지 않는다. 계약 §1-2 가 그것을 모든 item 응답의 키로 두었고,
 * 조건이 붙은 것은 `original_src` 하나다. 없는 규칙을 지어내지 않는다.
 *
 * ── `trace_texts` 도 같이 받친다 (계약 §9-4, 2026-08-31) ──
 * 그림 속 글자 목록은 트레이스 응답에 실린다고만 적혀 있고, **그 뒤의 PATCH·재요청 응답에도
 * 실리는지는 계약에 없다.** 안 실리는 쪽이면 통째로 갈아 끼울 때 목록이 사라지는데, 하필
 * 그 순간이 **사람이 방금 글자를 고쳐 저장한 직후**다 — 「6명」으로 바꿨더니 고치던 칸이
 * 통째로 없어진다. `original_src` 가 겪은 것과 똑같은 종류라 같은 처방을 쓴다.
 *
 * ⚠ **2026-09-01 실측: 서버는 이 키를 어느 응답에도 안 싣는다**(GET 목록·GET 이력·PATCH 셋 다
 * item 키가 14개로 같고 `trace_texts` 가 없다). 그래서 아래 받침은 **지금 한 번도 안 탄다** —
 * 지우지 않는 이유는 계약이 그 키를 약속하고 있어 서버가 실어 주기 시작하면 그날로 살아나기
 * 때문이다. 그때까지 화면이 쓰는 목록의 출처는 그림 자신이다(figureApi.readTraceTexts →
 * figureTrace). 즉 **이 파일이 없어도 칸은 뜬다** — 여기 있는 것은 서버가 말해 줄 때의 대비다.
 */
export function mergeFigureItem(prev, next) {
  if (!next) return prev ?? null;
  // `??` 여야 한다 — 서버가 보내는 「없음」이 `null` 이고, `||` 로 쓰면 빈 문자열도 앞 값으로
  // 되살아난다. 앞 값이 없으면 키를 지우지 않고 `null` 로 남긴다(있다/없다를 화면이 물어본다).
  const merged = {
    ...next,
    original_src: next.original_src ?? prev?.original_src ?? null,
    // 같은 규칙의 둘째 키 — 은행의 OCR 당시 지면 크롭(계약 §10-2 G5, 2026-09-05). 목록·이력 GET 에만 실린다.
    origin_crop: next.origin_crop ?? prev?.origin_crop ?? null,
  };
  // 목록은 **배열일 때만** 새 값이 이긴다 — `null`·없음은 「안 실어 보냈다」로 읽는다.
  // 서버가 「이제 트레이스가 아니다」를 빈 배열로 말할 수 있게 빈 배열은 그대로 받는다.
  if (!Array.isArray(next.trace_texts) && Array.isArray(prev?.trace_texts)) {
    merged.trace_texts = prev.trace_texts;
  }
  return merged;
}

export default mergeFigureItem;
